import {
  MdClose,
  MdDoneAll,
  MdError,
  MdOutlineCircle,
  MdTaskAlt,
} from "react-icons/md";
import clsx from "clsx";
import { RefObject, useEffect, useMemo, useState } from "react";
import useMobile from "../../hooks/useMobile";
import InfoBlock from "../InfoBlock/InfoBlock";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";
import { EInfoBlockKind } from "../InfoBlock/InfoBlock.types";

interface IField {
  name: string;
  completed: boolean;
  ref?: RefObject<HTMLElement | null>;
}

type FieldsValidationType = {
  fieldsToValidate: Array<IField>;
  onChange: (allCompleted: boolean) => void;
  showFields?: boolean;
  showButton?: boolean;
  message?: string;
  sortFieldsByName?: boolean;
};

const FieldsValidation: React.FC<FieldsValidationType> = ({
  fieldsToValidate,
  onChange,
  showFields = true,
  showButton = false,
  message = null,
  sortFieldsByName = false,
}) => {
  const { t } = useTranslation(["info_blocks"]);
  const { isMobile } = useMobile();
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const pendingToComplete = fieldsToValidate?.some(
    (field: IField) => !field?.completed,
  );

  const sortedFields = useMemo(() => {
    return sortFieldsByName
      ? sortBy(fieldsToValidate || [], ["name"])
      : fieldsToValidate;
  }, [fieldsToValidate, sortFieldsByName]);

  useEffect(() => {
    onChange(!pendingToComplete);
  }, [pendingToComplete, onChange]);

  return (
    <>
      <InfoBlock
        title={
          pendingToComplete
            ? t("info_blocks:fields_validation.incomplete.title")
            : t("info_blocks:fields_validation.complete.title")
        }
        displayMobileIcon
        // icon={
        //   pendingToComplete ? (
        //     // !showFields ? (
        //     <MdOutlineRemoveDone
        //       size={ICON_SIZES.MD}
        //       className="text-red-400"
        //     />
        //   ) : (
        //     // ) : null
        //     <MdDoneAll size={ICON_SIZES.MD} className="text-lime-400" />
        //   )
        // }
        kind={pendingToComplete ? EInfoBlockKind.ERROR : EInfoBlockKind.SUCCESS}
        message={
          pendingToComplete ? (
            <div className={clsx(["flex", "flex-col", "gap-2", "md:gap-2"])}>
              {pendingToComplete && (
                <div className={clsx(["text-xs", "md:text-xs", "w-full"])}>
                  {t("info_blocks:fields_validation.incomplete.message")}
                </div>
              )}
              {showFields && (
                <ul
                  className={clsx([
                    "md:flex",
                    "md:flex-row",
                    "gap-1",
                    "md:gap-y-1",
                    "md:gap-x-3",
                    "md:text-sm",
                    "text-sm",
                    "md:flex-wrap",
                    "grid",
                    "grid-cols-3",
                  ])}
                >
                  {sortedFields.map(
                    ({ name, completed, ref }: IField, index) => (
                      <li
                        key={index}
                        className={clsx([
                          "flex",
                          "flex-row",
                          "items-center",
                          "gap-1",
                          {
                            "cursor-pointer": !!ref?.current,
                          },
                        ])}
                        onClick={() => {
                          if (ref) {
                            ref?.current?.focus?.();
                          }
                        }}
                      >
                        {completed ? (
                          <MdTaskAlt
                            size={isMobile ? ICON_SIZES.XS : ICON_SIZES.SM}
                            className={clsx(["text-lime-500"])}
                          />
                        ) : (
                          <MdOutlineCircle
                            size={isMobile ? ICON_SIZES.XS : ICON_SIZES.SM}
                            className={clsx(["text-red-400"])}
                          />
                        )}
                        <div className={clsx(["text-xs", "md:text-xs"])}>
                          {name}
                        </div>
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>
          ) : (
            t("info_blocks:fields_validation.complete.message")
          )
        }
        buttons={[
          {
            tooltip: t("info_blocks:fields_validation.button"),
            icon: pendingToComplete ? (
              <MdError size={ICON_SIZES.SM} />
            ) : (
              <MdDoneAll size={ICON_SIZES.SM} />
            ),
            onClick: () => setShowStatusModal(true),
            hide: !showButton,
            kind: pendingToComplete ? EButtonKind.DANGER : EButtonKind.SAVE,
          },
        ]}
      />
      {showStatusModal && (
        <Modal
          onEscape={() => setShowStatusModal(false)}
          className="md:!max-w-[550px] md:!min-w-[450px]"
        >
          <Modal.Header
            onClose={() => setShowStatusModal(false)}
            title={t("info_blocks:fields_validation.modal_title")}
          />
          <Modal.Body>
            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-col",
                "gap-4",
                "md:gap-4",
                "pb-2",
              ])}
            >
              {message && (
                <div className={clsx(["text-sm", "md:text-sm"])}>{message}</div>
              )}
              <ul
                className={clsx([
                  "grid",
                  "w-full",
                  "grid-cols-2",
                  "md:grid-cols-2",
                  "gap-2",
                  "md:gap-2",
                  "md:text-sm",
                  "text-sm",
                ])}
              >
                {sortedFields.map(({ name, completed }: IField, index) => (
                  <li
                    key={index}
                    className={clsx([
                      "flex",
                      "flex-row",
                      "items-center",
                      "gap-1",
                    ])}
                  >
                    {completed ? (
                      <MdTaskAlt
                        size={ICON_SIZES.SM}
                        className={clsx(["text-lime-500"])}
                      />
                    ) : (
                      <MdOutlineCircle
                        size={ICON_SIZES.SM}
                        className={clsx(["text-red-400"])}
                      />
                    )}
                    <div
                      className={clsx([
                        "text-sm",
                        "md:text-sm",
                        {
                          "font-medium": !completed,
                        },
                      ])}
                    >
                      {name}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer
            leftContent={
              pendingToComplete ? null : (
                <div className="text-xs w-full">
                  {t("info_blocks:fields_validation.all_complete")}
                </div>
              )
            }
            actions={[
              {
                text: t("common:buttons.close"),
                onClick: () => setShowStatusModal(false),
                icon: <MdClose size={ICON_SIZES.SM} />,
              },
            ]}
          />
        </Modal>
      )}
    </>
  );
};

export default FieldsValidation;
